import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Table,
  Button,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
  TextField,
  Stack,
  styled,
  Divider,
  useTheme,
} from '@mui/material';

import moment from 'moment';

import useTable from '../../hooks/useTable';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { TableNoData, TableSkeleton, TableHeadCustom } from '../../components/table';

import conObj from '../../utils/connection-assistant';
import MyClaimTableRow from './MyClaimTableRow';
import TxnDateRangePicker from '../history/TxnDateRangePicker';
import useLocales from '../../hooks/useLocales';

const _filters = {
  dateFrom: '',
  dateTo: '',
  transactionType: '',
};

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#d4af37',
  fontWeight: 500,
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'black',
    },
    '&:hover fieldset': {
      borderColor: 'black',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'black',
      borderWidth: '2px',
    },
    color: 'black',
  },

  '& .MuiInputBase-input::placeholder': {
    color: 'gray',
  },
}));

const MyClaim = () => {
  const theme = useTheme();

  const { translate } = useLocales();

  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(0);

  const [totalRecord, setTotalRecord] = useState(0);

  const [filters, setFilters] = useState(_filters);

  const [isLoading, setIsLoading] = useState(false);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);

  const [amountInThousandths, setAmountInThousandths] = useState(false);

  const {
    dense,
    // page,
    order,
    orderBy,
    rowsPerPage,
    // setPage,
    //
    // selected,
    // setSelected,
    // onSelectRow,
    // onSelectAllRows,
    //
    onSort,
    // onChangeDense,
    // onChangePage,
    // onChangeRowsPerPage,
  } = useTable();

  useEffect(() => {
    getClaimHistory();
  }, [page, filters]);

  const getClaimHistory = () => {
    setIsLoading(true);
    const requestData = {
      'named-date_from': filters?.dateFrom ? moment(filters?.dateFrom).format('YYYY-MM-DD') : '',
      'named-date_to': filters?.dateTo ? moment(filters?.dateTo).format('YYYY-MM-DD') : '',
    };
    conObj
      .post(`members/claim_history.json?page=${page + 1}&limit=20`, requestData)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            setTableData(res.data?.data?.data);
            setTotalRecord(res.data?.data?.page.total_data);
          }
        }
      })
      .catch((err) => {
        console.log(`Error: ${err.message}`);
      })
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleFilterChange = (name, val) => {
    setFilters((prevState) => ({ ...prevState, [name]: val }));
  };

  const handleChangePage = (e, _page) => {
    setPage(_page);
  };

  const denseHeight = dense ? 60 : 80;

  const isNotFound = (!tableData.length && !!filters) || (!isLoading && !tableData.length);

  const TABLE_HEAD = [
    { id: 'title', label: translate('title'), align: 'left' },
    { id: 'date', label: translate('date'), align: 'left' },
    { id: 'amount', label: translate('amount'), align: 'right' },
  ];

  return (
    <Page title="My Claim">
      <Box
        component={Stack}
        sx={{ backgroundColor: '#000000' }}
        spacing={2}
        paddingBottom={{ xs: 12, md: 10 }}
        px={{ xs: 2, md: 20 }}
        py={2}
      >
        <Box mb={2}>
          <Box display="flex">
            <Box
              sx={{
                background: theme.palette.goldcity.gradient.main,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
              }}
              px={2}
              py={0.5}
            >
              <Typography>{translate('claim_history')}</Typography>
            </Box>
          </Box>
          <Divider sx={{ width: '100%', borderColor: theme.palette.goldcity.border.main }} />
        </Box>

        <Box sx={{ width: '100%' }}>
          <Stack
            direction={{ xl: 'row', lg: 'row', md: 'row', sm: 'row', xs: 'column' }}
            spacing={1}
            sx={{ mb: '5px' }}
          >
            <Stack direction="row" spacing={1}>
              <CustomTextField
                variant="outlined"
                value={filters?.dateFrom}
                onChange={() => {}}
                placeholder={translate('date_from')}
                size="small"
                onClick={() => setOpenDateRangePicker(true)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    position: 'relative',
                    background:
                      'linear-gradient(#333333, #0D0D0D) padding-box, ' +
                      'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%) border-box',
                    border: '2px solid transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: '#ccc',
                      opacity: 1,
                    },
                  },
                }}
              />

              <CustomTextField
                variant="outlined"
                value={filters?.dateTo}
                onChange={() => {}}
                placeholder={translate('date_to')}
                size="small"
                onClick={() => setOpenDateRangePicker(true)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    position: 'relative',
                    background:
                      'linear-gradient(#333333, #0D0D0D) padding-box, ' +
                      'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%) border-box',
                    border: '2px solid transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: '#ccc',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters(_filters);
                  setPage(0);
                }}
                sx={{
                  backgroundColor: 'white',
                  border: '1px solid black',
                  color: 'black',
                }}
              >
                {translate('clear')}
              </Button>
            </Stack>
          </Stack>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 400, position: 'relative' }}>
              <Table size={'small'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  onSort={onSort}
                  sx={{
                    background: '#d4af37',
                  }}
                />

                <TableBody>
                  {(isLoading ? [...Array(rowsPerPage)] : tableData).map((row, index) =>
                    row ? (
                      <MyClaimTableRow key={row.id} row={row} />
                    ) : (
                      !isNotFound && <TableSkeleton key={index} sx={{ height: denseHeight }} />
                    )
                  )}
                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={parseInt(totalRecord, 10)}
              rowsPerPage={20}
              page={page}
              onPageChange={handleChangePage}
              sx={{ color: '#fff' }}
            />
          </Box>
        </Box>
      </Box>
      {openDateRangePicker && (
        <TxnDateRangePicker
          open={openDateRangePicker}
          filters={filters}
          onFilterChange={setFilters}
          close={() => setOpenDateRangePicker(false)}
        />
      )}
    </Page>
  );
};

export default MyClaim;
